import { v4 as uuid } from "uuid";

import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import { ILayoutModuleConfiguration } from "@formant/core/model/ILayoutModuleConfiguration";
import { IModule } from "@formant/core/model/IModule";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { IRectangle } from "@formant/core/model/IRectangle";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";
import { ModuleType } from "@formant/core/model/ModuleType";

import { appStore } from "../stores";
import { defaultModuleSizes } from "./Observe/defaultModuleSizes";

type ILayout = IObserveLayout | ITeleopLayout;

interface ICustomModuleControllerProps {
    layout: IObserveLayout | ITeleopLayout;
    onChangeLayout: (layout: IObserveLayout) => void;
}

export interface ICustomModuleClosure {
    onAddDefinedCustomModule: (module: IModule) => string;
    onAddModule: (
        moduleType: ModuleType,
        config?: ILayoutModuleConfiguration<ModuleType>,
    ) => string;
    onRemoveModule: (moduleId: string) => void;
}

export const addModuleToBottomOfLayout = (
    _: ILayout,
    width: number,
    height: number,
): IRectangle => {
    return {
        x: 0,
        y: 0, // channel validator requires >= 0 here
        width,
        height,
    };
};

export const getDefaultSize = (module: AddableModuleType) => {
    const { height, width } = defaultModuleSizes[module] || {
        height: 3,
        width: 4,
    };
    return { height, width };
};

export function CustomModuleController(
    settings: ICustomModuleControllerProps,
): ICustomModuleClosure {
    const onRemoveModule = (moduleId: string) => {
        appStore.setConfirm({
            title: "Delete module",
            message: `Are you sure you'd like to delete this module?`,
            onOk: async () => {
                const newLayout: IObserveLayout = { ...settings.layout };
                delete newLayout[moduleId];
                settings.onChangeLayout(newLayout);
            },
        });
    };
    const onAddModule = (
        moduleType: AddableModuleType,
        config?: ILayoutModuleConfiguration<AddableModuleType>,
    ): string => {
        const { height, width } = getDefaultSize(moduleType);
        const moduleSettings = addModuleToBottomOfLayout(
            settings.layout,
            width,
            height,
        );
        const moduleId = "custom-module:" + uuid();
        settings.layout[moduleId] = {
            ...moduleSettings,
            configuration: {
                ...config,
                moduleType,
            },
        };
        return moduleId;
    };

    const onAddDefinedCustomModule = (module: IModule): string => {
        return onAddModule("custom", {
            customName: module.name,
            moduleType: "custom",
            customVisualizationUrl: module.url,
            parameters: {
                customModuleDefinitionId: module.id,
            },
        });
    };

    return {
        onAddDefinedCustomModule,
        onAddModule,
        onRemoveModule,
    };
}
